/**
 * Exchange Screen - coin list + portfolio summary
 * Dark exchange theme. Profit = red, loss = blue (Korean convention).
 */
import { COINS } from '../game/config/coins.js';
import { getChange24h, listedCoinIds } from '../game/state.js';
import {
  getHoldingsValue,
  getPnlPercent,
  getPortfolioPnlPercent,
  getTotalInvested,
  getUnrealizedPnl,
} from '../game/systems/tradingSystem.js';
import { formatNumber, formatPrice } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { coinIconHtml, coinDisplayName } from './coinIcon.js';
import { rescueHookHtml, runRescueHook } from './coinActionsPanel.js';
import { panicHookHtml, rewindHookHtml, bindRiskHooks } from './riskHooksPanel.js';
import { renderNewsTickerHtml, renderNewsSectionHtml, bindNewsFeedEvents } from './newsFeedPanel.js';
import { onRender, getState, setViewState, setInnerHTML } from './renderer.js';

let container: HTMLDivElement | null = null;

export function initExchangeScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'exchange-screen';
  container.className = 'screen-panel exchange-theme';
  content.appendChild(container);

  onRender(renderExchange, { alwaysTabs: ['exchange'] });
}

function pnlClass(value: number): string {
  if (value > 0) return 'ex-profit';
  if (value < 0) return 'ex-loss';
  return 'ex-flat';
}

function signed(value: number, decimals = 2): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}`;
}

function signedNum(value: number): string {
  return `${value >= 0 ? '+' : ''}${formatNumber(value)}`;
}

function renderPortfolioSummary(): string {
  const st = getState();
  const holdingsValue = getHoldingsValue(st.exchange);
  const invested = getTotalInvested(st.exchange);
  const totalAssets = st.coins + holdingsValue;
  const pnlAmount = holdingsValue - invested;
  const pnlPct = getPortfolioPnlPercent(st.exchange);
  const hasHoldings = Object.keys(st.exchange.holdings).length > 0;
  const cls = hasHoldings ? pnlClass(pnlAmount) : 'ex-flat';

  return `<div class="ex-portfolio">
    <div class="ex-portfolio-row">
      <span class="ex-label">${t('ex_total_assets')}</span>
      <span class="ex-portfolio-value">${formatNumber(totalAssets)}</span>
    </div>
    <div class="ex-portfolio-grid">
      <div><span class="ex-label">${t('ex_cash')}</span><span>${formatNumber(st.coins)}</span></div>
      <div><span class="ex-label">${t('ex_invested')}</span><span>${hasHoldings ? formatNumber(invested) : '-'}</span></div>
      <div><span class="ex-label">${t('ex_valuation')}</span><span>${hasHoldings ? formatNumber(holdingsValue) : '-'}</span></div>
      <div><span class="ex-label">${t('ex_eval_pnl')}</span><span class="${cls}">${hasHoldings ? `${signedNum(pnlAmount)} (${signed(pnlPct)}%)` : '-'}</span></div>
    </div>
  </div>`;
}

function renderHoldingRow(coinId: string): string {
  const st = getState();
  const cfg = COINS.find(c => c.id === coinId);
  const coin = st.exchange.coins[coinId];
  const holding = st.exchange.holdings[coinId];
  if (!cfg || !coin || !holding) return '';
  const listing = st.exchange.listing[coinId];
  const delisted = listing?.status !== 'listed';

  const value = holding.amount * coin.price;
  const pct = getPnlPercent(holding, coin.price);
  const pnl = getUnrealizedPnl(holding, coin.price);

  const hooks = delisted ? '' : panicHookHtml(coinId, true) + rescueHookHtml(coinId, true);
  const badge = delisted ? `<span class="ex-row-flag ex-flag-delist">${t('ex_delisted_badge')}</span>` : '';
  return `<div class="ex-coin-row${delisted ? ' ex-coin-row-delisted' : ''}" data-coin="${cfg.id}">
    ${coinIconHtml(cfg, false, listing)}
    <div class="ex-coin-info">
      <div class="ex-coin-name">${coinDisplayName(cfg, listing)} ${badge}</div>
      <div class="ex-coin-symbol">${formatNumber(holding.amount)} ${cfg.symbol}</div>
    </div>
    <div class="ex-coin-right">
      <div class="ex-coin-price">${formatNumber(value)}</div>
      <div class="ex-holding-pnl ${pnlClass(pct)}">${signedNum(pnl)} (${signed(pct)}%)</div>
    </div>
    ${hooks ? `<div class="ex-row-rescue">${hooks}</div>` : ''}
  </div>`;
}

function renderMyHoldings(): string {
  const st = getState();
  const held = COINS.filter(c => st.exchange.holdings[c.id]);
  if (held.length === 0) return '';

  let html = `<div class="ex-section-title">${t('ex_my_holdings')}</div>`;
  html += '<div class="ex-coin-list ex-holdings-list">';
  for (const cfg of held) {
    html += renderHoldingRow(cfg.id);
  }
  return html + '</div>';
}

/** Small pills on a coin row: rug warning + active manipulation + news direction */
function rowFlags(coinId: string): string {
  const st = getState();
  const now = Date.now();
  let flags = '';
  const rugPhase = st.risk.rugs[coinId]?.phase;
  if (rugPhase === 'crashing' || rugPhase === 'floor') {
    flags += `<span class="ex-row-flag ex-flag-rug">${t('ex_rug_badge')}</span>`;
  }
  const manip = st.market.activeManipulation;
  if (manip && manip.coinId === coinId && now < manip.expiresAt) {
    flags += `<span class="ex-row-flag ex-flag-manip">${t('ex_manip_badge')}</span>`;
  }
  const news = st.info.activeNews.find(n => n.coinId === coinId && now < n.expiresAt);
  if (news) {
    const cls = news.direction === 1 ? 'ex-profit' : 'ex-loss';
    flags += `<span class="ex-row-flag ex-flag-news ${cls}">${news.direction === 1 ? '↑' : '↓'}</span>`;
  }
  return flags;
}

function renderCoinRow(coinId: string): string {
  const st = getState();
  const cfg = COINS.find(c => c.id === coinId);
  const coin = st.exchange.coins[coinId];
  if (!cfg || !coin) return '';
  const listing = st.exchange.listing[coinId];

  const change = getChange24h(coin);
  const holding = st.exchange.holdings[coinId];
  let holdingBadge = '';
  if (holding) {
    const myPct = getPnlPercent(holding, coin.price);
    holdingBadge = `<span class="ex-holding-badge ${pnlClass(myPct)}">${t('ex_holding_badge')} ${signed(myPct, 1)}%</span>`;
  }
  const freshBadge = (listing && listing.everDelisted && listing.revivedCount > 0)
    ? `<span class="ex-row-flag ex-flag-revived">${t('ex_revived_badge')}</span>` : '';

  return `<div class="ex-coin-row" data-coin="${cfg.id}">
    ${coinIconHtml(cfg, false, listing)}
    <div class="ex-coin-info">
      <div class="ex-coin-name">${coinDisplayName(cfg, listing)} ${holdingBadge}${freshBadge}${rowFlags(coinId)}</div>
      <div class="ex-coin-symbol">${cfg.symbol}</div>
    </div>
    <div class="ex-coin-right">
      <div class="ex-coin-price">${formatPrice(coin.price)}</div>
      <div class="ex-coin-change ${pnlClass(change)}">${signed(change)}%</div>
    </div>
  </div>`;
}

function renderExchange() {
  if (!container) return;

  const holdingsHtml = renderMyHoldings();
  let html = `<div class="ex-header">${t('ex_title')}</div>`;
  html += renderNewsTickerHtml();
  html += renderPortfolioSummary();
  html += rewindHookHtml();
  html += renderNewsSectionHtml();
  html += `<button class="ex-leadroom-entry" data-goto-leadroom>
    <span class="ex-leadroom-title">${t('lr_entry_title')}</span>
    <span class="ex-leadroom-sub">${t('lr_entry_sub')}</span>
  </button>`;
  html += `<button class="ex-leadroom-entry" data-goto-leaderboard>
    <span class="ex-leadroom-title">${t('lb_entry_title')}</span>
    <span class="ex-leadroom-sub">${t('lb_entry_sub')}</span>
  </button>`;
  html += holdingsHtml;
  if (holdingsHtml) html += `<div class="ex-section-title">${t('ex_all_coins')}</div>`;
  html += '<div class="ex-coin-list">';
  for (const coinId of listedCoinIds(getState().exchange)) {
    html += renderCoinRow(coinId);
  }
  html += '</div>';

  if (setInnerHTML(container, html)) {
    bindNewsFeedEvents(container);
    container.querySelectorAll('[data-coin]').forEach(row => {
      row.addEventListener('click', () => {
        const coinId = (row as HTMLElement).dataset.coin!;
        playSfx('tap');
        setViewState({ currentTab: 'coindetail', selectedCoinId: coinId });
      });
    });
    container.querySelectorAll('[data-rescue-coin]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        void runRescueHook((btn as HTMLElement).dataset.rescueCoin!);
      });
    });
    container.querySelector('[data-goto-leadroom]')?.addEventListener('click', () => {
      playSfx('tap');
      setViewState({ currentTab: 'leadroom' });
    });
    container.querySelector('[data-goto-leaderboard]')?.addEventListener('click', () => {
      playSfx('tap');
      setViewState({ currentTab: 'leaderboard' });
    });
    bindRiskHooks(container);
  }
}
